<template>
  <div class="recommended-page">
    <!-- Hero header -->
    <div class="rec-hero">
      <button class="rec-back" @click="goBack">
        <i class="fas fa-arrow-left"></i> Back
      </button>
      <div class="rec-hero-brand">
        <div class="rec-hero-icon"><i class="fas fa-box-open"></i></div>
        <div class="rec-hero-text">
          <h1>Recommended for you</h1>
          <p>Products suggested by your PettyCash advisor</p>
        </div>
      </div>
      <div v-if="products.length" class="rec-hero-count">
        <span class="rec-count-num">{{ products.length }}</span>
        <span class="rec-count-label">Product{{ products.length > 1 ? 's' : '' }}</span>
      </div>
    </div>

    <!-- Quick signup gate -->
    <quick-signup-modal
      v-model="showQuickSignup"
      :redirect-path="$route.path"
    />

    <!-- Loading skeletons -->
    <div v-if="loading" class="rec-grid">
      <div v-for="n in 6" :key="'sk' + n" class="rec-card rec-skeleton">
        <div class="rec-card-img skeleton-block"></div>
        <div class="rec-card-body">
          <div class="skeleton-line w70"></div>
          <div class="skeleton-line w40"></div>
          <div class="skeleton-line w90"></div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="rec-state">
      <div class="rec-state-ico"><i class="fas fa-exclamation-triangle"></i></div>
      <h3>Something went wrong</h3>
      <p>{{ error }}</p>
      <button class="rec-retry" @click="fetchProducts"><i class="fas fa-redo"></i> Try again</button>
    </div>

    <!-- Empty -->
    <div v-else-if="!products.length" class="rec-state">
      <div class="rec-state-ico"><i class="fas fa-box-open"></i></div>
      <h3>No products yet</h3>
      <p>This message doesn't contain any marketplace products.</p>
    </div>

    <!-- Products grid -->
    <template v-else>
      <div class="rec-sub">
        <span class="rec-sub-title"><i class="fas fa-th-large"></i> Catalog</span>
        <span class="rec-sub-price-note">All prices in FCFA</span>
      </div>

      <div class="rec-grid">
        <article
          v-for="(prod, idx) in products"
          :key="'rec-' + prod.id"
          :id="'rec-prod-' + prod.id"
          class="rec-card"
          :class="{ 'rec-highlight': highlightId == prod.id }"
        >
          <!-- Image -->
          <button class="rec-card-media" @click="openDetail(prod)">
            <img v-if="getProductImage(prod)" :src="getProductImage(prod)" :alt="prod.name" loading="lazy" />
            <div v-else class="rec-media-ph"><i class="fas fa-box-open"></i></div>
            <span v-if="prod.category" class="rec-cat-badge">{{ prod.category }}</span>
            <span v-if="stockStatus(prod) === 'out'" class="rec-stock-badge out">Out of stock</span>
            <span v-else-if="stockStatus(prod) === 'low'" class="rec-stock-badge low">{{ prod.quantity }} left</span>
            <div class="rec-media-zoom"><i class="fas fa-expand"></i></div>
          </button>

          <!-- Body -->
          <div class="rec-card-body">
            <h3 class="rec-card-name" @click="openDetail(prod)">{{ prod.name }}</h3>
            <div class="rec-card-price-row">
              <span class="rec-card-price">{{ formatPrice(prod.price) }}</span>
              <span v-if="prod.no_bought" class="rec-card-sold">
                <i class="fas fa-shopping-cart"></i> {{ prod.no_bought }} sold
              </span>
            </div>

            <p v-if="plainDescription(prod)" class="rec-card-desc">{{ truncate(plainDescription(prod), 90) }}</p>

            <div class="rec-card-actions">
              <button class="rec-btn rec-btn-save" @click="startSaving(prod)">
                <i class="fas fa-piggy-bank"></i> Save to Buy
              </button>
              <button class="rec-btn rec-btn-cart" :disabled="adding === prod.id" @click="addToBag(prod)">
                <i v-if="adding === prod.id" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-shopping-basket"></i>
                Add to Bag
              </button>
              <button class="rec-btn rec-btn-pay" :disabled="paying === prod.id" @click="payNow(prod)">
                <i v-if="paying === prod.id" class="fas fa-spinner fa-spin"></i>
                <i v-else class="fas fa-credit-card"></i>
                Buy Now
              </button>
            </div>
          </div>
        </article>
      </div>
    </template>

    <!-- Product detail modal -->
    <v-dialog
      v-model="detailDialog"
      max-width="780px"
      :fullscreen="$vuetify && $vuetify.breakpoint ? $vuetify.breakpoint.xs : true"
      content-class="rec-detail-dialog"
      retain-focus="false"
    >
      <div v-if="activeProduct" class="rec-detail">
        <div class="rec-detail-hero">
          <div class="rec-detail-media">
            <img v-if="getProductImage(activeProduct)" :src="getProductImage(activeProduct)" :alt="activeProduct.name" />
            <div v-else class="rec-media-ph"><i class="fas fa-box-open"></i></div>
          </div>
          <button class="rec-detail-close" @click="detailDialog = false">
            <i class="fas fa-times"></i>
          </button>
          <span v-if="activeProduct.category" class="rec-cat-badge">{{ activeProduct.category }}</span>
          <span v-if="stockStatus(activeProduct) === 'out'" class="rec-stock-badge out">Out of stock</span>
          <span v-else-if="stockStatus(activeProduct) === 'low'" class="rec-stock-badge low">{{ activeProduct.quantity }} left</span>
        </div>

        <div class="rec-detail-body">
          <h2 class="rec-detail-name">{{ activeProduct.name }}</h2>
          <div class="rec-detail-price-row">
            <span class="rec-detail-price">{{ formatPrice(activeProduct.price) }}</span>
            <span v-if="activeProduct.no_bought" class="rec-detail-sold">
              <i class="fas fa-shopping-cart"></i> {{ activeProduct.no_bought }} sold
            </span>
          </div>

          <div class="rec-detail-meta">
            <span v-if="activeProduct.quantity !== undefined && activeProduct.quantity !== null">
              <i class="fas fa-cube"></i> Stock: {{ activeProduct.quantity }}
            </span>
            <span v-if="activeProduct.get_date_str"><i class="fas fa-calendar"></i> Listed {{ activeProduct.get_date_str }}</span>
          </div>

          <div v-if="activeProduct.description" class="rec-detail-desc" v-html="activeProduct.description"></div>

          <div class="rec-detail-actions">
            <button class="rec-btn rec-btn-save" @click="startSaving(activeProduct)">
              <i class="fas fa-piggy-bank"></i> Save to Buy
            </button>
            <button class="rec-btn rec-btn-cart" :disabled="adding === activeProduct.id" @click="addToBag(activeProduct)">
              <i v-if="adding === activeProduct.id" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-shopping-basket"></i>
              Add to Bag
            </button>
            <button class="rec-btn rec-btn-pay" :disabled="paying === activeProduct.id" @click="payNow(activeProduct)">
              <i v-if="paying === activeProduct.id" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-credit-card"></i>
              Buy Now
            </button>
          </div>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import QuickSignupModal from '../Items/QuickSignupModal.vue'

export default {
  name: 'RecommendedProducts',
  components: { QuickSignupModal },
  data () {
    return {
      products: [],
      loading: true,
      error: null,
      highlightId: null,
      detailDialog: false,
      activeProduct: null,
      adding: null,
      paying: null,
      showQuickSignup: false
    }
  },
  mounted () {
    this.highlightId = this.$route.query.highlight || null
    this.fetchProducts()
    document.title = 'PettyCash | Recommended Products'
  },
  watch: {
    '$route.query.highlight' (val) {
      this.highlightId = val || null
      if (val) this.scrollToProduct(val)
    }
  },
  methods: {
    async fetchProducts () {
      this.loading = true
      this.error = null
      try {
        const msgId = this.$route.params.messageId
        if (!msgId) {
          this.error = 'Missing message reference.'
          this.loading = false
          return
        }
        const res = await axios.get('/api/v1/market_place/get/product/recommended/' + msgId + '/')
        this.products = res.data.products || []
        this.$nextTick(() => {
          if (this.highlightId) this.scrollToProduct(this.highlightId)
          this.refreshTranslation()
        })
      } catch (e) {
        console.error('Failed to load recommended products:', e)
        this.error = 'We could not load the products. Please try again.'
      } finally {
        this.loading = false
      }
    },

    goBack () {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push('/market_place')
    },

    scrollToProduct (id) {
      const el = document.getElementById('rec-prod-' + id)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    },

    // ── helpers ──
    getProductImage (prod) {
      return prod.get_image || prod.image || prod.thumbnail || null
    },
    plainDescription (prod) {
      if (!prod.description) return ''
      const d = document.createElement('div')
      d.innerHTML = prod.description
      return (d.textContent || d.innerText || '').trim()
    },
    stockStatus (prod) {
      const q = prod.quantity
      if (q === undefined || q === null) return 'unknown'
      if (q <= 0) return 'out'
      if (q <= 5) return 'low'
      return 'in'
    },
    formatPrice (value) {
      if (value === null || value === undefined || value === '') return ''
      const num = Number(value)
      if (isNaN(num)) return String(value)
      return num.toLocaleString('fr-FR') + ' FCFA'
    },
    truncate (text, max) {
      return text.length > max ? text.substring(0, max) + '…' : text
    },

    // ── auth ──
    isAuthenticated () {
      return !!this.$store.state.isAuthenticated
    },
    ensureAuth (callback) {
      if (!this.isAuthenticated()) {
        this.showQuickSignup = true
        return false
      }
      if (typeof callback === 'function') callback()
      return true
    },

    // ── actions ──
    openDetail (prod) {
      this.activeProduct = prod
      this.detailDialog = true
      this.$nextTick(() => this.refreshTranslation())
    },

    refreshTranslation () {
      if (this.$translator && typeof this.$translator.refreshIfEnabled === 'function') {
        this.$translator.refreshIfEnabled('dom', this.$el)
      }
    },

    addToBag (prod) {
      this.ensureAuth(() => {
        this.adding = prod.id
        const item = { product: prod, quantity: 1 }
        this.$store.commit('addToShoppingBag', item)
        this.$store.commit('setSnackBarMessage', prod.name + ' added to your shopping bag')
        this.$store.commit('setSnackBarColor', 'teal darken-4')
        this.$store.commit('activateSnackBar', true)
        this.adding = null
      })
    },

    startSaving (prod) {
      this.ensureAuth(() => {
        this.$store.commit('setSaveItem', prod.id)
        this.$router.push('/market_place/campaign/create/new_campaign')
      })
    },

    async payNow (prod) {
      if (!this.ensureAuth()) return
      if (prod.quantity !== undefined && prod.quantity !== null && prod.quantity < 1) {
        this.toast('This product is out of stock', 'red darken-4')
        return
      }
      this.paying = prod.id
      try {
        const bag = {
          items: [{ product: prod, quantity: 1 }]
        }
        const response = await axios.post(
          '/api/v1/market_place/request/bill/transaction',
          JSON.stringify(bag),
          { headers: { 'Content-Type': 'application/json' } }
        )
        const data = response.data && response.data[0]
        if (data) {
          this.toast(data.text, data.color || 'teal darken-4')
          if (data.valid && data.requested_order_id) {
            this.$router.push('/market_place/shopping_bag/items/pay_now/' + data.requested_order_id)
          }
        }
      } catch (e) {
        console.error('Pay now error:', e)
        this.toast('An error occurred. Please try again later.', 'red darken-4')
      } finally {
        this.paying = null
      }
    },

    toast (msg, color) {
      this.$store.commit('setSnackBarMessage', msg)
      this.$store.commit('setSnackBarColor', color || 'teal darken-4')
      this.$store.commit('activateSnackBar', true)
    }
  }
}
</script>

<style scoped>
.recommended-page {
  min-height: calc(100vh - 70px);
  background:
    radial-gradient(1200px 300px at 20% -10%, rgba(0, 150, 136, 0.08), transparent 60%),
    radial-gradient(900px 260px at 90% -10%, rgba(79, 195, 247, 0.08), transparent 60%),
    #f4f7f9;
  padding: 0 0 56px;
}

/* ── Hero ── */
.rec-hero {
  position: relative;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 26px 22px 30px;
  background: linear-gradient(120deg, #004d40 0%, #00897b 55%, #26a69a 100%);
}

.rec-hero::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(500px 160px at 85% 0%, rgba(255, 255, 255, 0.14), transparent 70%);
  pointer-events: none;
}

.rec-back {
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;
}

.rec-back:hover { background: rgba(255, 255, 255, 0.26); }

.rec-hero-brand {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.rec-hero-icon {
  width: 52px;
  height: 52px;
  min-width: 52px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
}

.rec-hero-text { min-width: 0; }

.rec-hero-text h1 {
  margin: 0;
  color: #fff;
  font-size: 21px;
  font-weight: 800;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rec-hero-text p {
  margin: 3px 0 0;
  color: rgba(255, 255, 255, 0.88);
  font-size: 12.5px;
}

.rec-hero-count {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: baseline;
  gap: 5px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 14px;
  padding: 6px 14px;
  backdrop-filter: blur(4px);
}

.rec-count-num { font-size: 22px; font-weight: 900; color: #fff; }
.rec-count-label { font-size: 11.5px; font-weight: 700; color: rgba(255, 255, 255, 0.9); }

/* ── States ── */
.rec-state {
  text-align: center;
  padding: 70px 20px 40px;
}

.rec-state-ico {
  width: 84px;
  height: 84px;
  margin: 0 auto 18px;
  border-radius: 26px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 38px;
  color: #b0bec5;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.08);
}

.rec-state h3 { font-size: 18px; font-weight: 800; color: #37474f; margin: 0 0 6px; }
.rec-state p { font-size: 13.5px; color: rgba(0, 0, 0, 0.5); margin: 0; }

.rec-retry {
  margin-top: 16px;
  padding: 10px 22px;
  background: #00897b;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s ease;
}

.rec-retry:hover { background: #00796b; }

/* ── Sub header ── */
.rec-sub {
  max-width: 1100px;
  margin: 26px auto 14px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rec-sub-title {
  font-size: 14px;
  font-weight: 800;
  color: #37474f;
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.rec-sub-title i { color: #00897b; }

.rec-sub-price-note { font-size: 11.5px; font-weight: 600; color: rgba(0, 0, 0, 0.4); }

/* ── Grid ── */
.rec-grid {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 18px;
}

/* ── Card ── */
.rec-card {
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.rec-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 36px rgba(38, 50, 56, 0.14);
}

.rec-card.rec-highlight {
  border: 2px solid #00897b;
  box-shadow: 0 0 0 4px rgba(0, 137, 123, 0.16);
  animation: recPulse 1.2s ease-in-out 3;
}

@keyframes recPulse {
  0%, 100% { box-shadow: 0 0 0 4px rgba(0, 137, 123, 0.16); }
  50% { box-shadow: 0 0 0 10px rgba(0, 137, 123, 0.05); }
}

/* Media */
.rec-card-media {
  position: relative;
  width: 100%;
  height: 190px;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.07), rgba(0, 100, 80, 0.12));
  border: none;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  display: block;
}

.rec-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.rec-card:hover .rec-card-media img { transform: scale(1.06); }

.rec-media-ph {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 54px;
  color: rgba(0, 137, 123, 0.2);
}

.rec-cat-badge {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 10.5px;
  font-weight: 700;
  color: #fff;
  background: rgba(0, 0, 0, 0.52);
  padding: 3px 10px;
  border-radius: 10px;
  backdrop-filter: blur(4px);
  text-transform: capitalize;
}

.rec-stock-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 10.5px;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 10px;
  backdrop-filter: blur(4px);
}

.rec-stock-badge.out { background: #f44336; color: #fff; }
.rec-stock-badge.low { background: #ff9800; color: #fff; }

.rec-media-zoom {
  position: absolute;
  right: 12px;
  bottom: 12px;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.92);
  color: #00897b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transform: translateY(6px);
  transition: all 0.25s ease;
}

.rec-card:hover .rec-media-zoom { opacity: 1; transform: translateY(0); }

/* Body */
.rec-card-body {
  padding: 15px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.rec-card-name {
  font-size: 15px;
  font-weight: 800;
  color: #263238;
  line-height: 1.3;
  margin: 0;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.rec-card-price-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.rec-card-price { font-size: 17px; font-weight: 900; color: #00897b; }

.rec-card-sold {
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.rec-card-desc {
  font-size: 12.5px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.55);
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Actions */
.rec-card-actions {
  display: flex;
  gap: 8px;
  margin-top: auto;
  padding-top: 8px;
}

.rec-btn {
  flex: 1;
  padding: 9px 8px;
  border: none;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
  letter-spacing: 0;
  text-transform: none;
}

.rec-btn-cart {
  background: #00897b;
  color: #fff;
}
.rec-btn-cart:hover:not(:disabled) { background: #00796b; }

.rec-btn-save {
  background: rgba(0, 137, 123, 0.09);
  color: #00796b;
  border: 1px solid rgba(0, 137, 123, 0.25);
}
.rec-btn-save:hover:not(:disabled) { background: rgba(0, 137, 123, 0.16); }

.rec-btn-pay {
  background: #d32f2f;
  color: #fff;
}
.rec-btn-pay:hover:not(:disabled) { background: #b71c1c; }

.rec-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Skeleton ── */
.rec-skeleton { pointer-events: none; }

.skeleton-block {
  background: linear-gradient(100deg, #eceff1 40%, #f5f8fa 50%, #eceff1 60%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.skeleton-line {
  height: 14px;
  border-radius: 8px;
  margin-top: 8px;
  background: linear-gradient(100deg, #eceff1 40%, #f5f8fa 50%, #eceff1 60%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
.w70 { width: 70%; }
.w40 { width: 40%; }
.w90 { width: 90%; }

/* ── Detail dialog ── */
.rec-detail {
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.rec-detail-hero {
  position: relative;
  height: 300px;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.08), rgba(0, 100, 80, 0.14));
  display: flex;
  align-items: center;
  justify-content: center;
}

.rec-detail-media { width: 100%; height: 100%; }
.rec-detail-media img { width: 100%; height: 100%; object-fit: contain; }

.rec-detail-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: none;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 15px;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: all 0.2s ease;
}

.rec-detail-close:hover { background: rgba(0, 0, 0, 0.75); transform: scale(1.05); }

.rec-detail-body { padding: 22px 24px 26px; }

.rec-detail-name { font-size: 21px; font-weight: 800; color: #263238; margin: 0; }

.rec-detail-price-row { display: flex; align-items: center; gap: 12px; margin-top: 8px; }
.rec-detail-price { font-size: 22px; font-weight: 900; color: #00897b; }
.rec-detail-sold { font-size: 12px; font-weight: 700; color: rgba(0, 0, 0, 0.45); }

.rec-detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  padding: 10px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.5);
}

.rec-detail-meta span { display: inline-flex; align-items: center; gap: 5px; }
.rec-detail-meta i { color: #00897b; font-size: 11px; }

.rec-detail-desc {
  margin-top: 14px;
  font-size: 14px;
  line-height: 1.6;
  color: rgba(38, 50, 56, 0.85);
}

.rec-detail-desc >>> img { max-width: 100%; border-radius: 8px; }

.rec-detail-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
  flex-wrap: wrap;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .rec-hero { padding: 18px 14px 22px; gap: 12px; flex-wrap: wrap; }
  .rec-hero-brand { order: 2; flex-basis: 100%; }
  .rec-hero-text h1 { font-size: 17px; }
  .rec-hero-count { order: 3; }
  .rec-grid { grid-template-columns: 1fr; padding: 0 12px; gap: 14px; }
  .rec-sub { padding: 20px 12px 10px; }
  .rec-card-actions { flex-wrap: wrap; }
  .rec-btn { flex: 1 1 45%; }
  .rec-detail-body { padding: 16px 16px 20px; }
  .rec-detail-hero { height: 220px; }
  .rec-detail-actions .rec-btn { flex: 1 1 45%; }
}
</style>